import { useMemo, useState } from "react";
import { GLOBAL_SCHEMAS, PIPES, checkExpressionSyntax, humanizeKey, type DataSource, type FieldSchema, type FieldType } from "@broadpaper/core";
import { Icon, FIELD_TYPE_ICONS } from "../ui/icons.js";
import { Button, TextInput } from "../ui/primitives.js";
import { useSession } from "../state/context.js";

export interface PickerEntry {
  expr: string;
  label: string;
  path: string[];
  type: FieldType;
  schema: FieldSchema;
  depth: number;
  group: string;
}

/** Flattens data sources (and scope variables) into pickable entries. */
export function flattenSchemas(
  dataSources: DataSource[],
  extra: Record<string, FieldSchema> = {},
  opts: { includeGlobals?: boolean; arraysOnly?: boolean; types?: FieldType[]; themeMeta?: Record<string, unknown> } = {}
): PickerEntry[] {
  const out: PickerEntry[] = [];
  const visit = (expr: string, schema: FieldSchema, labels: string[], depth: number, group: string) => {
    const label = labels.join(" › ");
    const isObject = schema.type === "object";
    const wanted = opts.arraysOnly ? schema.type === "array" : !opts.types || opts.types.includes(schema.type) || schema.type === "object" || schema.type === "array";
    if (!isObject && wanted) out.push({ expr, label, path: labels, type: schema.type, schema, depth, group });
    if (isObject && schema.fields) for (const [k, f] of Object.entries(schema.fields)) visit(`${expr}.${k}`, f, [...labels, f.label ?? k], depth + 1, group);
    if (schema.type === "array" && schema.itemSchema?.type === "object" && schema.itemSchema.fields && !opts.arraysOnly) {
      // Expose projections (e.g. holdings.value) for aggregates.
      for (const [k, f] of Object.entries(schema.itemSchema.fields)) if (f.type !== "object" && f.type !== "array") out.push({ expr: `${expr}.${k}`, label: [...labels, `each ${f.label ?? k}`].join(" › "), path: [...labels, f.label ?? k], type: "array", schema: { type: "array", itemSchema: f }, depth: depth + 1, group });
    }
  };
  for (const [name, schema] of Object.entries(extra)) visit(name, schema, [schema.label ?? name], 0, "Current item");
  for (const s of dataSources) {
    if (s.schema && Object.keys(s.schema).length) for (const [k, f] of Object.entries(s.schema)) visit(`${s.id}.${k}`, f, [s.label, f.label ?? k], 1, s.label);
  }
  if (opts.includeGlobals !== false && !opts.arraysOnly) {
    for (const [name, schema] of Object.entries(GLOBAL_SCHEMAS)) {
      // `theme` is skipped as a whole: colours, typography and table defaults are
      // a styling system, not data, and a picker full of `theme.colors.chart` is
      // a picker nobody can find a field in.
      if (name === "theme") continue;
      visit(name, schema, [schema.label ?? name], 0, "Document");
    }

    /*
     * The business's own details, which are the half of the theme that *is* data.
     *
     * `theme.meta` is free-form by design — a host puts whatever its brand needs
     * in there — so `GLOBAL_SCHEMAS` declares it as an object with no fields and
     * there is nothing to enumerate from the schema. The live theme has the keys,
     * though, so they are read off it: whatever this host actually set is exactly
     * what should be offerable.
     *
     * Without this the picker could not reach them at all, so a rule like "show
     * the small print when there is small print" was unwritable — while the
     * starter templates were doing precisely that, by hand, in expressions a user
     * had no way to author.
     */
    for (const key of Object.keys(opts.themeMeta ?? {})) {
      const value = (opts.themeMeta ?? {})[key];
      if (value !== null && typeof value === "object") continue;
      out.push({
        expr: `theme.meta.${key}`,
        label: humanizeKey(key),
        path: ["Your brand", humanizeKey(key)],
        type: "string",
        schema: { type: "string", label: humanizeKey(key) },
        depth: 1,
        group: "Your brand"
      });
    }
  }
  return out;
}

export interface DataPickerProps {
  dataSources: DataSource[];
  /** Scope variables (repeater item etc.). */
  scopeVars?: Record<string, FieldSchema>;
  arraysOnly?: boolean;
  types?: FieldType[];
  /** Currently bound expression, shown in the manual box. */
  value?: string;
  onPick: (expr: string, label: string, entry?: PickerEntry) => void;
  onClear?: () => void;
  allowExpression?: boolean;
}

/**
 * How a value can be shown, per type, in the words of the result rather than of
 * the pipe.
 *
 * Exported because the calculation builder and the table's column totals both need
 * the same list: a format offered in one place and typed as syntax in another is
 * how `totalFormat` ended up as a monospace box asking a business user for
 * `currency:"GBP":0`.
 */
export const FORMAT_SUGGESTIONS: Record<string, Array<{ label: string; pipe: string }>> = {
  currency: [{ label: "£ 12,345.67", pipe: "currency" }, { label: "£ 12,346", pipe: "currency:\"GBP\":0" }],
  number: [{ label: "12,345.67", pipe: "number" }, { label: "12,346", pipe: "number:0" }],
  percent: [{ label: "12.3%", pipe: "percent" }, { label: "12%", pipe: "percent:0" }],
  date: [{ label: "20 May 2026", pipe: 'date:"d MMMM yyyy"' }, { label: "20/05/2026", pipe: 'date:"dd/MM/yyyy"' }, { label: "May 2026", pipe: 'date:"MMMM yyyy"' }],
  datetime: [{ label: "20 May 2026, 14:30", pipe: 'datetime:"d MMM yyyy, HH:mm"' }, { label: "Date only", pipe: 'date:"d MMMM yyyy"' }],
  boolean: [{ label: "Yes / No", pipe: "boolean" }, { label: "Custom words", pipe: 'boolean:"Yes":"No"' }],
  string: [{ label: "UPPERCASE", pipe: "upper" }, { label: "Title Case", pipe: "title" }],
  array: [{ label: "Count", pipe: "length" }, { label: "Join with commas", pipe: 'join:", "' }]
};

export function DataPicker({ dataSources, scopeVars, arraysOnly, types, value, onPick, onClear, allowExpression = true }: DataPickerProps) {
  const [q, setQ] = useState("");
  const [expr, setExpr] = useState(value ?? "");
  const [pending, setPending] = useState<PickerEntry | null>(null);
  // The live theme, for the brand details under `theme.meta`. Read here rather
  // than threaded through every caller: the picker is always inside a session.
  const themeMeta = useSession().activeTheme.meta;
  const entries = useMemo(
    () => flattenSchemas(dataSources, scopeVars, { arraysOnly, types, ...(themeMeta ? { themeMeta } : {}) }),
    [dataSources, scopeVars, arraysOnly, types, themeMeta]
  );
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((e) => e.label.toLowerCase().includes(needle) || e.expr.toLowerCase().includes(needle));
  }, [entries, q]);
  const groups = useMemo(() => {
    const m = new Map<string, PickerEntry[]>();
    for (const e of filtered) m.set(e.group, [...(m.get(e.group) ?? []), e]);
    return [...m.entries()];
  }, [filtered]);
  const syntaxError = expr.trim() ? checkExpressionSyntax(expr) : null;

  if (pending) {
    const suggestions = FORMAT_SUGGESTIONS[pending.type] ?? [];
    return (
      <div>
        <div className="bpe-picker__search" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button size="sm" icon="chevron-right" style={{ transform: "rotate(180deg)" }} tooltip="Back" onClick={() => setPending(null)} />
          <span className="bpe-truncate" style={{ fontWeight: 600 }}>{pending.label}</span>
        </div>
        <div className="bpe-picker__list">
          <div className="bpe-picker__group">Format</div>
          <button type="button" className="bpe-data-field" onClick={() => onPick(pending.expr, pending.label, pending)}>
            <Icon name="check" className="bpe-data-field__icon" /> As is
          </button>
          {suggestions.map((s) => (
            <button key={s.pipe} type="button" className="bpe-data-field" onClick={() => onPick(`${pending.expr} | ${s.pipe}`, pending.label, pending)}>
              <Icon name="wand" className="bpe-data-field__icon" /> {s.label}
              {/* The pipe itself is a developer's detail: useful beside the label
                  where there is an expression box to type it into, and syntax for
                  its own sake where there is not. */}
              {allowExpression ? (
                <span className="bpe-data-field__type bpe-mono" style={{ textTransform: "none" }}>| {s.pipe}</span>
              ) : null}
            </button>
          ))}
          {pending.type !== "array" && (
            <button type="button" className="bpe-data-field" onClick={() => onPick(`${pending.expr} | default:"—"`, pending.label, pending)}>
              <Icon name="eraser" className="bpe-data-field__icon" /> With fallback “—” when empty
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bpe-picker__search">
        <TextInput value={q} onChange={setQ} placeholder={arraysOnly ? "Search lists…" : "Search fields…"} autoFocus aria-label="Search fields" />
      </div>
      <div className="bpe-picker__list" role="listbox">
        {groups.length === 0 ? <div className="bpe-empty">No matching fields</div> : null}
        {groups.map(([group, items]) => (
          <div key={group}>
            <div className="bpe-picker__group">{group}</div>
            {/* The tooltip below is the raw path, which is the right thing to offer
                a developer and a field code to everybody else — so where there is
                no expression editor it is the readable label instead. */}
            {items.map((e) => (
              <button key={e.expr} type="button" role="option" aria-selected={value === e.expr} className="bpe-data-field" style={{ paddingLeft: 6 + Math.max(0, e.depth - 1) * 10 }} title={allowExpression ? e.expr : e.label} onClick={() => (arraysOnly || e.type === "array" || e.type === "object" ? onPick(e.expr, e.label, e) : setPending(e))}>
                <Icon name={FIELD_TYPE_ICONS[e.type] ?? "box"} className="bpe-data-field__icon" />
                <span className="bpe-truncate">{e.path[e.path.length - 1]}</span>
                <span className="bpe-data-field__type">{e.type}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
      {allowExpression ? (
        <div className="bpe-picker__foot">
          <TextInput value={expr} onChange={setExpr} mono placeholder="or type an expression" invalid={!!syntaxError} aria-label="Expression" onKeyDown={(e) => e.key === "Enter" && !syntaxError && expr.trim() && onPick(expr.trim(), expr.trim())} />
          <Button size="sm" variant="primary" disabled={!!syntaxError || !expr.trim()} onClick={() => onPick(expr.trim(), expr.trim())}>Use</Button>
          {onClear && value ? <Button size="sm" icon="x" tooltip="Clear binding" onClick={onClear} /> : null}
        </div>
      ) : null}
    </div>
  );
}

export const PIPE_NAMES = Object.keys(PIPES);
